import {
  ScheduledReport,
  ScheduledReportCycle,
  ScheduledReportMonth,
  ScheduledReportRun,
  ScheduledReportRunStatus,
  ScheduledReportType,
} from 'redux/automatedReporting/automatedReportingTypes'
import { Segment } from 'redux/employer/employerTypes'
import { arrangeAllSegmentsForSelection } from 'features/Programs/Shared/helperFunctions'
import { SegmentListType } from 'features/Programs/Shared/types'
import moment, { Moment } from 'moment'

export interface ScheduledReportForm {
  name: string
  reportType: ScheduledReportType
  cycle: ScheduledReportCycle
  nextReportDate: string
  reportStartMonth: ScheduledReportMonth
  reportEndMonth: ScheduledReportMonth
  wholeMonth: boolean
  reportStartDay: string
  reportEndDay: string
  recipients: string
  isActive: boolean
}

export type ScheduledReportFormField = keyof ScheduledReportForm

export const initialScheduledReportForm: ScheduledReportForm = {
  name: '',
  reportType: 'reimbursements',
  cycle: 'monthly',
  nextReportDate: '',
  reportStartMonth: 'previous',
  reportEndMonth: 'previous',
  wholeMonth: true,
  reportStartDay: '',
  reportEndDay: '',
  recipients: '',
  isActive: true,
}

export const scheduledReportTypeItems = [
  { id: 'reimbursements', text: 'Reimbursements' },
  { id: 'allocations', text: 'Allocations' },
]

export const scheduledReportCycleItems = [{ id: 'monthly', text: 'Monthly' }]

export const scheduledReportMonthItems = [
  { id: 'previous', text: 'Previous Month' },
  { id: 'current', text: 'Current Month' },
  { id: 'next', text: 'Next Month' },
]

const reportMonthOffsets: Record<ScheduledReportMonth, number> = {
  previous: -1,
  current: 0,
  next: 1,
}

export const parseScheduledReportRecipients = (recipients: string) =>
  recipients
    .split(/[,\n]/)
    .map((recipient) => recipient.trim())
    .filter(Boolean)

export const isWholeMonthRange = (scheduledReport?: ScheduledReport | null) =>
  !scheduledReport ||
  scheduledReport.reportStartDay == null ||
  scheduledReport.reportEndDay == null

export const getScheduledReportWindowBounds = (
  nextReportDate: string,
  reportStartMonth: ScheduledReportMonth,
  reportEndMonth: ScheduledReportMonth,
  reportStartDay: number | null,
  reportEndDay: number | null
): { startDate: Moment; endDate: Moment } => {
  const startMonth = moment(nextReportDate)
    .startOf('month')
    .add(reportMonthOffsets[reportStartMonth], 'month')
  const endMonth = moment(nextReportDate)
    .startOf('month')
    .add(reportMonthOffsets[reportEndMonth], 'month')
  const startDay = Math.min(reportStartDay || 1, startMonth.daysInMonth())
  const endDay = Math.min(reportEndDay || endMonth.daysInMonth(), endMonth.daysInMonth())

  return {
    startDate: startMonth.clone().date(startDay),
    endDate: endMonth.clone().date(endDay),
  }
}

export const getInitialScheduledReportForm = (
  scheduledReport?: ScheduledReport | null
): ScheduledReportForm => {
  const wholeMonth = isWholeMonthRange(scheduledReport)

  return {
    name: scheduledReport?.name || initialScheduledReportForm.name,
    reportType:
      scheduledReport?.reportType || initialScheduledReportForm.reportType,
    cycle: scheduledReport?.cycle || initialScheduledReportForm.cycle,
    nextReportDate:
      scheduledReport?.nextReportDate ||
      initialScheduledReportForm.nextReportDate,
    reportStartMonth:
      scheduledReport?.reportStartMonth ||
      initialScheduledReportForm.reportStartMonth,
    reportEndMonth:
      scheduledReport?.reportEndMonth ||
      initialScheduledReportForm.reportEndMonth,
    wholeMonth,
    reportStartDay: wholeMonth
      ? initialScheduledReportForm.reportStartDay
      : String(scheduledReport?.reportStartDay),
    reportEndDay: wholeMonth
      ? initialScheduledReportForm.reportEndDay
      : String(scheduledReport?.reportEndDay),
    recipients:
      scheduledReport?.recipients?.join(', ') ||
      initialScheduledReportForm.recipients,
    isActive: scheduledReport?.isActive ?? initialScheduledReportForm.isActive,
  }
}

export const getScheduledReportSelectedSegments = (
  allSegments: Segment[] | null,
  scheduledReport?: ScheduledReport | null
) =>
  arrangeAllSegmentsForSelection(allSegments || []).filter((segment) =>
    scheduledReport?.segments?.includes(segment.id)
  )

export const getScheduledReportSegmentItems = (allSegments: Segment[] | null) =>
  arrangeAllSegmentsForSelection(allSegments || [])

const isValidDay = (day: string) => {
  const parsedDay = Number(day)
  return Number.isInteger(parsedDay) && parsedDay >= 1 && parsedDay <= 31
}

export const getScheduledReportFormValidation = (
  form: ScheduledReportForm,
  isSaving: boolean
) => {
  const recipients = parseScheduledReportRecipients(form.recipients)
  const reportStartDay = Number(form.reportStartDay)
  const reportEndDay = Number(form.reportEndDay)
  const reportEndMonth = form.wholeMonth ? form.reportStartMonth : form.reportEndMonth
  const isStartDayInvalid = !form.wholeMonth && !isValidDay(form.reportStartDay)
  const isEndDayInvalid = !form.wholeMonth && !isValidDay(form.reportEndDay)
  const isNameInvalid = !form.name.trim()
  const isDateInvalid = !form.nextReportDate
  const areRecipientsInvalid = recipients.length === 0
  const { startDate, endDate } = form.nextReportDate
    ? getScheduledReportWindowBounds(
        form.nextReportDate,
        form.reportStartMonth,
        reportEndMonth,
        form.wholeMonth ? null : reportStartDay,
        form.wholeMonth ? null : reportEndDay
      )
    : { startDate: moment.invalid(), endDate: moment.invalid() }
  const isRangeInvalid =
    !form.wholeMonth &&
    !isDateInvalid &&
    !isStartDayInvalid &&
    !isEndDayInvalid &&
    startDate.isAfter(endDate)

  return {
    recipients,
    reportStartDay,
    reportEndDay,
    reportEndMonth,
    isNameInvalid,
    isDateInvalid,
    isStartDayInvalid,
    isEndDayInvalid,
    isRangeInvalid,
    areRecipientsInvalid,
    isSubmitDisabled:
      isSaving ||
      isNameInvalid ||
      isDateInvalid ||
      isStartDayInvalid ||
      isEndDayInvalid ||
      isRangeInvalid ||
      areRecipientsInvalid,
  }
}

export const buildScheduledReportPayload = (
  form: ScheduledReportForm,
  selectedSegments: SegmentListType[],
  isEditing: boolean
): ScheduledReport => {
  const { recipients, reportStartDay, reportEndDay, reportEndMonth } =
    getScheduledReportFormValidation(form, false)

  return {
    name: form.name.trim(),
    reportType: form.reportType,
    cycle: form.cycle,
    nextReportDate: form.nextReportDate,
    reportStartMonth: form.reportStartMonth,
    reportEndMonth,
    reportStartDay: form.wholeMonth ? null : reportStartDay,
    reportEndDay: form.wholeMonth ? null : reportEndDay,
    recipients,
    segments: selectedSegments.map((segment) => segment.id),
    fileFormat: 'csv',
    ...(isEditing ? { isActive: form.isActive } : {}),
  }
}

export const formatReportType = (reportType: ScheduledReport['reportType']) => {
  const reportTypeLabels = {
    reimbursements: 'Reimbursements',
    allocations: 'Allocations',
  }

  return reportTypeLabels[reportType]
}

export const formatCycle = (cycle: ScheduledReport['cycle']) => {
  const cycleLabels = {
    monthly: 'Monthly',
  }

  return cycleLabels[cycle]
}

export const formatScheduledReportRange = (
  scheduledReport: Pick<
    ScheduledReport,
    | 'nextReportDate'
    | 'reportStartMonth'
    | 'reportEndMonth'
    | 'reportStartDay'
    | 'reportEndDay'
  >
) => {
  const { startDate, endDate } = getScheduledReportWindowBounds(
    scheduledReport.nextReportDate,
    scheduledReport.reportStartMonth,
    scheduledReport.reportEndMonth,
    scheduledReport.reportStartDay,
    scheduledReport.reportEndDay
  )

  return `${startDate.format('MM/DD/YYYY')} - ${endDate.format('MM/DD/YYYY')}`
}

export const getSegmentNamesById = (
  allSegments: Segment[] | null
): Record<string, string> =>
  (allSegments || []).reduce<Record<string, string>>(
    (segmentMap, segment) => ({
      ...segmentMap,
      [segment.id]: segment.name,
    }),
    {}
  )

export const formatSegmentNames = (
  scheduledReport: ScheduledReport,
  segmentNamesById: Record<string, string>
) => {
  const segmentNames =
    scheduledReport.segmentNames && scheduledReport.segmentNames.length > 0
      ? scheduledReport.segmentNames
      : scheduledReport.segments
          .map((segmentId) => segmentNamesById[segmentId])
          .filter(Boolean)

  return segmentNames.length > 0 ? segmentNames.join(', ') : 'All Segments'
}

export const formatScheduledReportRunStatus = (
  status: ScheduledReportRunStatus
) => {
  const statusLabels = {
    in_progress: 'In Progress',
    success: 'Success',
    failed: 'Failed',
  }

  return statusLabels[status]
}

export const getScheduledReportRunStatusChipVariant = (
  status: ScheduledReportRunStatus
) => {
  const statusVariants = {
    in_progress: 'blue',
    success: 'green',
    failed: 'red',
  } as const

  return statusVariants[status]
}

export const formatScheduledReportRunDate = (date?: string | null) =>
  date ? moment(date).format('MM/DD/YYYY') : '-'

export const formatScheduledReportRunDateTime = (date?: string | null) =>
  date ? moment(date).format('MM/DD/YYYY h:mm A') : '-'

export const formatScheduledReportRunDataWindow = (
  scheduledReportRun: Pick<
    ScheduledReportRun,
    'dataWindowStart' | 'dataWindowEndInclusive'
  >
) =>
  `${formatScheduledReportRunDate(
    scheduledReportRun.dataWindowStart
  )} - ${formatScheduledReportRunDate(
    scheduledReportRun.dataWindowEndInclusive
  )}`

export const formatScheduledReportRunRecipients = (
  scheduledReportRun: Pick<ScheduledReportRun, 'recipients'>
) =>
  scheduledReportRun.recipients.length > 0
    ? scheduledReportRun.recipients.join(', ')
    : '-'

export const formatScheduledReportRunSegments = (
  scheduledReportRun: Pick<ScheduledReportRun, 'segments'>
) =>
  scheduledReportRun.segments.length > 0
    ? scheduledReportRun.segments.map((segment) => segment.name).join(', ')
    : 'All Segments'
